/**
 * Gift with purchase: keep only the highest qualifying gift in the cart.
 * Requests are debounced and cart.js polling is avoided so Cloudflare is less likely to challenge add-to-cart.
 */
const ATTR_EARTIP = "gwp_eartip_variant_id";
const ATTR_CHOICE = "gwp_tier1000_product_id";
const SYNC_DELAY = 800;
const RELOAD_GUARD_KEY = "gwp_cart_reload_guard";

const config = () => window.themeVariables?.gwp || {};

const moneyFormatter = () => new Intl.NumberFormat(window.Shopify?.locale || undefined, {
  style: "currency",
  currency: window.Shopify?.currency?.active || "USD"
});

let syncing = false;
let resyncQueued = false;
let lastFailedVariantId = null;
let lastCart = config().initialCart || { items: [], attributes: {}, item_count: 0 };
let syncTimer = null;
let forcedChoiceProductId = null;

const storageGet = (key) => {
  try {
    return sessionStorage.getItem(key) || "";
  } catch (error) {
    return "";
  }
};

const storageSet = (key, value) => {
  try {
    sessionStorage.setItem(key, String(value));
  } catch (error) {
    // Private browsing can block sessionStorage.
  }
};

const storageRemove = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    // Private browsing can block sessionStorage.
  }
};

const isGwpItem = (item) => String(item?.properties?._gwp) === "true";

const getQualifyingTotal = (cart) => {
  return (cart.items || []).reduce((sum, item) => {
    if (isGwpItem(item)) {
      return sum;
    }
    return sum + (item.final_line_price || 0);
  }, 0);
};

const convertedThreshold = (tier) => {
  return Math.round((tier.threshold || 0) * (window.Shopify?.currency?.rate || 1));
};

const usableTiers = () => {
  return (config().tiers || [])
    .map((tier) => ({
      ...tier,
      products: (tier.products || []).filter((product) => product && product.id && product.variants?.length)
    }))
    .filter((tier) => tier.products.length)
    .map((tier) => ({ ...tier, threshold: convertedThreshold(tier) }))
    .sort((a, b) => a.threshold - b.threshold);
};

const getActiveTier = (qualifyingTotal) => {
  const tiers = usableTiers();
  let active = null;
  for (const tier of tiers) {
    if (qualifyingTotal >= tier.threshold) {
      active = tier;
    }
  }
  return active;
};

const availableVariants = (product) => {
  const variants = product?.variants || [];
  const inStock = variants.filter((variant) => variant.available);
  return inStock.length ? inStock : variants;
};

const variantForProduct = (product) => availableVariants(product)[0] || null;

const productForVariant = (tier, variantId) => {
  return (tier.products || []).find((product) => product.variants?.some((variant) => Number(variant.id) === Number(variantId)));
};

const rememberedValue = (cart, key) => {
  const fromCart = cart.attributes?.[key];
  if (fromCart != null && String(fromCart) !== "") {
    return String(fromCart);
  }
  return storageGet(key);
};

const currentGift = (cart) => (cart?.items || []).find(isGwpItem) || null;

const currentGiftProductId = (cart) => {
  const gift = currentGift(cart);
  return gift?.product_id ? String(gift.product_id) : "";
};

const paintChoice = (productId) => {
  document.querySelectorAll("[data-gwp-choice]").forEach((element) => {
    element.classList.toggle("is-selected", String(element.getAttribute("data-gwp-choice")) === String(productId));
  });
};

const pickVariantId = (tier, cart) => {
  if (!tier) {
    return null;
  }

  if (tier.mode === "random") {
    const product = tier.products[0];
    const variants = availableVariants(product);
    if (!variants.length) {
      return null;
    }

    // Keep the gift already in cart stable across reloads / private browsing.
    const existingGift = currentGift(cart);
    if (existingGift && variants.some((variant) => Number(variant.id) === Number(existingGift.variant_id))) {
      return Number(existingGift.variant_id);
    }

    const saved = Number.parseInt(rememberedValue(cart, ATTR_EARTIP), 10);
    if (saved && variants.some((variant) => Number(variant.id) === saved)) {
      return saved;
    }

    return variants[Math.floor(Math.random() * variants.length)].id;
  }

  if (tier.mode === "choice") {
    const products = (tier.products || []).filter((product) => variantForProduct(product));
    const preferredId = String(forcedChoiceProductId || rememberedValue(cart, ATTR_CHOICE) || currentGiftProductId(cart) || "");
    const product = products.find((item) => String(item.id) === preferredId) || products[0];
    return variantForProduct(product)?.id || null;
  }

  return variantForProduct(tier.products[0])?.id || null;
};

const persistLocalChoice = (tier, variantId) => {
  if (!tier || !variantId) {
    return;
  }
  if (tier.mode === "random") {
    storageSet(ATTR_EARTIP, variantId);
  }
  if (tier.mode === "choice") {
    const productId = productForVariant(tier, variantId)?.id;
    if (productId) {
      storageSet(ATTR_CHOICE, productId);
    }
  }
};

const buildAttributeUpdates = (tier, cart, variantId) => {
  const attributes = {};

  if (tier?.mode === "choice") {
    const choiceProductId = String(forcedChoiceProductId || productForVariant(tier, variantId)?.id || "");
    if (choiceProductId && String(cart.attributes?.[ATTR_CHOICE] || "") !== choiceProductId) {
      attributes[ATTR_CHOICE] = choiceProductId;
    }
  }

  if (tier?.mode === "random" && variantId) {
    if (String(cart.attributes?.[ATTR_EARTIP] || "") !== String(variantId)) {
      attributes[ATTR_EARTIP] = String(variantId);
    }
  }

  return attributes;
};

const sectionsToBundle = () => {
  const sections = [];
  document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
    bubbles: true,
    detail: { sections }
  }));
  return sections;
};

const cartRoot = () => window.Shopify?.routes?.root || "/";

const isCartPage = () => window.themeVariables?.settings?.pageType === "cart";

const fetchCart = async () => {
  const cart = await (await fetch(`${cartRoot()}cart.js`)).json();
  return rememberCart(cart);
};

const postCart = async (path, body) => {
  const response = await fetch(`${cartRoot()}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body)
  });
  const json = await response.json();
  if (!response.ok) {
    throw new Error(json.description || json.message || "Cart request failed");
  }
  return json;
};

const rememberCart = (cart) => {
  if (cart?.items) {
    lastCart = cart;
  }
  return lastCart;
};

const publishCart = (cart, extraSections) => {
  rememberCart(cart);
  if (extraSections) {
    cart.sections = extraSections;
  }
  if (cart.sections && Object.keys(cart.sections).length > 0) {
    document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
      bubbles: true,
      detail: {
        baseEvent: "gwp:sync",
        cart
      }
    }));
    return;
  }
  document.dispatchEvent(new CustomEvent("cart:refresh"));
};

const reloadCartPageIfNeeded = (itemsChanged) => {
  if (!itemsChanged || !isCartPage()) {
    storageRemove(RELOAD_GUARD_KEY);
    return;
  }

  // Allow at most two reloads in a sync chain so duplicate cleanup can paint,
  // without re-entering an infinite refresh loop.
  const reloadCount = Number.parseInt(storageGet(RELOAD_GUARD_KEY) || "0", 10) || 0;
  if (reloadCount >= 2) {
    storageRemove(RELOAD_GUARD_KEY);
    console.warn("[GWP] Skipped repeated cart reload after gift sync");
    return;
  }

  storageSet(RELOAD_GUARD_KEY, String(reloadCount + 1));
  window.location.reload();
};

const planGiftUpdates = (cart, variantId) => {
  const gwpItems = (cart.items || []).filter(isGwpItem);
  const updates = {};
  let keptMatchingKey = null;

  for (const item of gwpItems) {
    if (!item.key) {
      continue;
    }

    const isMatch = Boolean(variantId) && Number(item.variant_id) === Number(variantId);
    if (!isMatch) {
      updates[item.key] = 0;
      continue;
    }

    // Only one gift line is allowed, even if the same variant was added twice.
    if (keptMatchingKey) {
      updates[item.key] = 0;
      continue;
    }

    keptMatchingKey = item.key;
    if (item.quantity !== 1) {
      updates[item.key] = 1;
    }
  }

  return {
    updates,
    hasGift: Boolean(keptMatchingKey),
    gwpCount: gwpItems.length
  };
};

const applyGwp = async (cart) => {
  // Always re-read the server cart so queued/stale snapshots cannot add a second gift.
  let latestCart = await fetchCart();
  if (cart?.attributes) {
    latestCart = {
      ...latestCart,
      attributes: {
        ...(latestCart.attributes || {}),
        ...cart.attributes
      }
    };
  }

  const qualifyingTotal = getQualifyingTotal(latestCart);
  const tier = getActiveTier(qualifyingTotal);
  const variantId = pickVariantId(tier, latestCart);
  persistLocalChoice(tier, variantId);

  let { updates, hasGift } = planGiftUpdates(latestCart, variantId);
  let needAdd = Boolean(variantId) && !hasGift && lastFailedVariantId !== variantId;
  let needUpdate = Object.keys(updates).length > 0;
  const attributeUpdates = buildAttributeUpdates(tier, latestCart, variantId);
  const needAttributes = Object.keys(attributeUpdates).length > 0;

  if (!needAdd && !needUpdate && !needAttributes) {
    storageRemove(RELOAD_GUARD_KEY);
    return false;
  }

  const sections = sectionsToBundle();
  let itemsChanged = false;

  if (needUpdate || needAttributes) {
    const payload = {};
    if (needUpdate) {
      payload.updates = updates;
      itemsChanged = true;
    }
    if (needAttributes) {
      payload.attributes = attributeUpdates;
    }
    if (!needAdd && sections.length) {
      payload.sections = sections;
    }
    latestCart = await postCart("cart/update.js", payload);
    rememberCart(latestCart);
  }

  if (needAdd) {
    // Re-check right before add — another sync may have inserted the gift already.
    latestCart = await fetchCart();
    ({ updates, hasGift } = planGiftUpdates(latestCart, variantId));
    if (Object.keys(updates).length) {
      latestCart = await postCart("cart/update.js", { updates });
      rememberCart(latestCart);
      itemsChanged = true;
      hasGift = planGiftUpdates(latestCart, variantId).hasGift;
    }
    needAdd = Boolean(variantId) && !hasGift && lastFailedVariantId !== variantId;
  }

  if (needAdd) {
    try {
      const addPayload = {
        items: [{
          id: variantId,
          quantity: 1,
          properties: {
            _gwp: "true",
            _gwp_tier: String(tier.id)
          }
        }]
      };
      if (sections.length) {
        addPayload.sections = sections;
      }
      const added = await postCart("cart/add.js", addPayload);
      lastFailedVariantId = null;
      forcedChoiceProductId = null;
      itemsChanged = true;

      // Prefer the real cart after add so we never publish a merged duplicate snapshot.
      latestCart = await fetchCart();
      const leftover = planGiftUpdates(latestCart, variantId);
      if (Object.keys(leftover.updates).length) {
        latestCart = await postCart("cart/update.js", {
          updates: leftover.updates,
          sections: sections.length ? sections : undefined
        });
        rememberCart(latestCart);
        publishCart(latestCart);
      } else {
        publishCart(latestCart, added.sections);
      }
    } catch (error) {
      lastFailedVariantId = variantId;
      console.warn("[GWP] Could not add gift", error);
      if (needUpdate || needAttributes) {
        publishCart(latestCart);
      }
      // Do not treat add failure as a successful mutation — avoids reload loops.
      return false;
    }
  } else if (itemsChanged || needAttributes) {
    publishCart(latestCart);
  }

  return itemsChanged;
};

const syncGwp = async (cart) => {
  if (!config().enabled || window.Shopify?.designMode) {
    return false;
  }

  if (cart) {
    rememberCart(cart);
  }

  if (syncing) {
    // Never reuse a stale cart snapshot — just request another fresh sync.
    resyncQueued = true;
    return false;
  }

  syncing = true;
  document.documentElement.classList.add("gwp-syncing");

  let mutated = false;
  try {
    mutated = await applyGwp(cart || lastCart);
  } catch (error) {
    console.warn("[GWP]", error);
    mutated = false;
  } finally {
    syncing = false;
    document.documentElement.classList.remove("gwp-syncing");
    if (resyncQueued) {
      resyncQueued = false;
      const queuedMutated = await syncGwp(null);
      mutated = mutated || queuedMutated;
    }
  }

  return mutated;
};

const scheduleSync = (cart) => {
  if (cart) {
    rememberCart(cart);
  }
  clearTimeout(syncTimer);
  return new Promise((resolve) => {
    syncTimer = setTimeout(async () => {
      // Fresh server cart avoids double-adding from outdated drawer events.
      const mutated = await syncGwp(null);
      resolve(mutated);
    }, SYNC_DELAY);
  });
};

const formatTemplate = (template, vars) => {
  return (template || "").replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => vars[key] ?? "");
};

const remainingHtml = (amount) => {
  return `<span class="bold text-accent">${moneyFormatter().format(amount / 100)}</span>`;
};

const giftHtml = (name) => `<span class="bold">${name || ""}</span>`;

const updateGwpBar = (element, cart) => {
  const qualifyingTotal = getQualifyingTotal(cart);
  const tiers = usableTiers();
  const active = getActiveTier(qualifyingTotal);
  const next = tiers.find((tier) => qualifyingTotal < tier.threshold);
  const strings = window.themeVariables?.strings || {};
  const messageElement = element.querySelector(".gwp-bar__message");
  const progressBar = element.querySelector("progress-bar");

  let message = "";
  let valueMax = tiers[0]?.threshold || qualifyingTotal || 1;

  if (!active && next) {
    message = formatTemplate(strings.gwpSpendMore, {
      remaining_amount: remainingHtml(next.threshold - qualifyingTotal),
      gift: giftHtml(next.name)
    });
    valueMax = next.threshold;
  } else if (active && next) {
    message = formatTemplate(strings.gwpUnlockedNext, {
      current_gift: giftHtml(active.name),
      remaining_amount: remainingHtml(next.threshold - qualifyingTotal),
      next_gift: giftHtml(next.name)
    });
    valueMax = next.threshold;
  } else if (active) {
    message = formatTemplate(strings.gwpUnlockedMax, {
      gift: giftHtml(active.name)
    });
    valueMax = active.threshold;
  }

  if (messageElement) {
    messageElement.innerHTML = message;
  }

  if (progressBar) {
    progressBar.valueMax = valueMax;
    progressBar.valueNow = qualifyingTotal;
  }

  element.setAttribute("total-price", String(qualifyingTotal));
  paintChoice(currentGiftProductId(cart) || forcedChoiceProductId || rememberedValue(cart, ATTR_CHOICE));
};

class GwpBar extends HTMLElement {
  connectedCallback() {
    this._onCartChanged = (event) => {
      if (event.detail?.cart) {
        updateGwpBar(this, event.detail.cart);
      }
    };
    document.addEventListener("cart:change", this._onCartChanged);
    updateGwpBar(this, lastCart);
  }

  disconnectedCallback() {
    document.removeEventListener("cart:change", this._onCartChanged);
  }
}

if (!window.customElements.get("gwp-bar")) {
  window.customElements.define("gwp-bar", GwpBar);
}

const onCartChanged = (event) => {
  if (event.detail?.baseEvent === "gwp:sync" || !event.detail?.cart) {
    return;
  }
  scheduleSync(event.detail.cart).then((mutated) => {
    reloadCartPageIfNeeded(mutated);
  });
};

const onChoiceClick = (event) => {
  const button = event.target.closest("[data-gwp-choice]");
  if (!button) {
    return;
  }
  event.preventDefault();
  const productId = button.getAttribute("data-gwp-choice");
  if (!productId) {
    return;
  }

  paintChoice(productId);
  storageSet(ATTR_CHOICE, productId);
  lastFailedVariantId = null;
  forcedChoiceProductId = productId;
  clearTimeout(syncTimer);
  syncTimer = null;

  const applyChoice = async () => {
    const cart = await fetchCart();
    cart.attributes = {
      ...(cart.attributes || {}),
      [ATTR_CHOICE]: String(productId)
    };
    rememberCart(cart);
    if (String(currentGiftProductId(cart)) === String(productId)) {
      forcedChoiceProductId = null;
      paintChoice(productId);
      return false;
    }
    return syncGwp(cart);
  };

  applyChoice().then((mutated) => {
    reloadCartPageIfNeeded(mutated);
  }).catch((error) => console.warn("[GWP]", error));
};

const onCheckoutSubmit = (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.querySelector('[name="checkout"]')) {
    return;
  }
  if (!syncing && !syncTimer) {
    return;
  }
  event.preventDefault();
  const waitAndSubmit = async () => {
    clearTimeout(syncTimer);
    syncTimer = null;
    if (!syncing) {
      await syncGwp(lastCart);
    }
    const waitForIdle = setInterval(() => {
      if (!syncing) {
        clearInterval(waitForIdle);
        form.submit();
      }
    }, 50);
  };
  waitAndSubmit();
};

document.addEventListener("cart:change", onCartChanged);
document.addEventListener("click", onChoiceClick);
document.addEventListener("submit", onCheckoutSubmit, true);

const boot = async () => {
  if (!config().enabled || window.Shopify?.designMode) {
    return;
  }
  if (!isCartPage()) {
    return;
  }
  try {
    const mutated = await syncGwp(lastCart);
    reloadCartPageIfNeeded(mutated);
  } catch (error) {
    console.warn("[GWP]", error);
  }
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot);
} else {
  boot();
}
